import { AuthView, Message } from '../../hooks/useAuthForm';
import AuthMessage from './AuthMessage';
import GoogleSignInButton from './GoogleSignInButton';
import { getAthenaUrl } from '../../config/platformUrls';

interface AuthFormProps {
  view: AuthView;
  email: string;
  password: string;
  loading: boolean;
  message: Message;
  onEmailChange: (email: string) => void;
  onPasswordChange: (password: string) => void;
  onSubmit: (view: AuthView) => Promise<void>;
  onGoogleSignIn: () => Promise<void>;
}

function getButtonText(view: AuthView): string {
  switch (view) {
    case 'sign_in':
      return 'Sign in';
    case 'sign_up':
      return 'Get started';
    case 'forgotten_password':
      return 'Send reset instructions';
    case 'update_password':
      return 'Update password';
    default:
      return 'Submit';
  }
}

export default function AuthForm({
  view,
  email,
  password,
  loading,
  message,
  onEmailChange,
  onPasswordChange,
  onSubmit,
  onGoogleSignIn,
}: AuthFormProps) {
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit(view);
  };

  return (
    <div className="px-6 pb-6">
      {/* Google Sign In */}
      <div className="mb-4">
        <GoogleSignInButton onClick={onGoogleSignIn} />
      </div>

      {/* Divider */}
      <div className="relative mb-4">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-3 bg-theme-light-bg dark:bg-theme-dark-bg text-theme-light-text-secondary dark:text-theme-dark-text-secondary">
            {view === 'sign_up' ? 'or get started with email' : 'or continue with email'}
          </span>
        </div>
      </div>

      {/* Email/Password Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        {view !== 'update_password' && (
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-theme-light-text-primary dark:text-theme-dark-text-primary mb-1"
            >
              {view === 'sign_up' ? 'Your email address' : 'Email address'}
            </label>
            <input
              id="email"
              name="email"
              type="email"
              value={email}
              onChange={(e) => onEmailChange(e.target.value)}
              data-ph-capture-attribute="input-login-email"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-theme-light-bg dark:bg-theme-dark-bg text-theme-light-text-primary dark:text-theme-dark-text-primary placeholder-theme-light-text-muted dark:placeholder-theme-dark-text-muted focus:outline-none focus:ring-2 focus:ring-[#E34290]/20 focus:border-[#E34290]"
              placeholder={
                view === 'sign_up' ? 'Enter your email to get started' : 'Enter your email'
              }
              required
            />
          </div>
        )}

        {view !== 'forgotten_password' && (
          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-theme-light-text-primary dark:text-theme-dark-text-primary mb-1"
            >
              {view === 'update_password'
                ? 'New password'
                : view === 'sign_up'
                  ? 'Create a password'
                  : 'Password'}
            </label>
            <input
              id="password"
              name="password"
              type="password"
              value={password}
              data-ph-capture-attribute="input-login-password"
              onChange={(e) => onPasswordChange(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-theme-light-bg dark:bg-theme-dark-bg text-theme-light-text-primary dark:text-theme-dark-text-primary placeholder-theme-light-text-muted dark:placeholder-theme-dark-text-muted focus:outline-none focus:ring-2 focus:ring-[#E34290]/20 focus:border-[#E34290]"
              placeholder={
                view === 'update_password'
                  ? 'Create a new password'
                  : view === 'sign_up'
                    ? 'Choose a secure password'
                    : 'Enter your password'
              }
              required
            />
          </div>
        )}

        {/* Message Display */}
        <AuthMessage message={message} />

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-[#E34290] disabled:bg-[#E34290]/50 text-white font-medium py-2 px-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E34290]/20 focus:ring-offset-2 focus:ring-offset-theme-light-bg dark:focus:ring-offset-theme-dark-bg"
          data-ph-capture-attribute="btn-login-submit"
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Loading...
            </div>
          ) : (
            getButtonText(view)
          )}
        </button>
      </form>

      {/* Athena Redirect Link - Only show for sign_in view */}
      {view === 'sign_in' && (
        <div className="mt-4">
          <div className="relative mb-3">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-3 bg-theme-light-bg dark:bg-theme-dark-bg text-theme-light-text-secondary dark:text-theme-dark-text-secondary">
                or
              </span>
            </div>
          </div>
          <a
            href={getAthenaUrl()}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full border-2 border-[#E34290] text-[#E34290] font-medium py-2 px-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E34290]/30 focus:ring-offset-2 focus:ring-offset-theme-light-bg dark:focus:ring-offset-theme-dark-bg inline-block text-center hover:bg-[#E34290]/10 transition-colors"
            data-ph-capture-attribute="btn-login-athena-redirect"
          >
            New to Jua? Get started with Athena →
          </a>
        </div>
      )}
    </div>
  );
}
